import React, { useEffect, useState } from "react";
import authFetch from '../api';
import { Trash2, PlusCircle, Users as UsersIcon, Search } from "lucide-react";
import { useTranslation } from 'react-i18next';
import ProtectedRoute from '../components/ProtectedRoute';

const AdminUsersContent = () => {
  const { t } = useTranslation();
  const [users, setUsers] = useState([]);
  const [deletedUsers, setDeletedUsers] = useState([]);
  const [activeTab, setActiveTab] = useState("active");
  const [showModal, setShowModal] = useState(false);
  const [useDefaultAvatar, setUseDefaultAvatar] = useState(true);
  const [customAvatar, setCustomAvatar] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    role: "Employee",
    avatar: ""
  });
  const [selectedUser, setSelectedUser] = useState(null);
  const [userProjects, setUserProjects] = useState([]);
  const [userAssets, setUserAssets] = useState([]);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [pendingSpecialPermissions, setPendingSpecialPermissions] = useState({});
  const [savingPermissions, setSavingPermissions] = useState(false);
  const [userInventoryStatus, setUserInventoryStatus] = useState({});

  // Filter users based on search query
  const getFilteredUsers = () => {
    let filtered = activeTab === "active" ? users : deletedUsers;
    
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(user => 
        user.name?.toLowerCase().includes(q) ||
        user.email?.toLowerCase().includes(q) ||
        user.role?.toLowerCase().includes(q)
      );
    }
    
    return filtered;
  };

  const displayedUsers = getFilteredUsers();

  // Debug information
  console.log('🔍 AdminUsers Debug:', {
    usersCount: users.length,
    deletedUsersCount: deletedUsers.length,
    activeTab,
    selectedUser: selectedUser?.name,
    userProjectsCount: userProjects.length,
    userAssetsCount: userAssets.length,
    userInventoryStatus: Object.keys(userInventoryStatus).length
  });

  useEffect(() => {
    fetchUsers();
    fetchDeletedUsers();
    fetchUserInventoryStatus();
  }, []);

  const fetchUsers = async () => {
    try {
      console.log('🔍 Fetching users...');
      const res = await authFetch('/users');
      
      let usersData;
      if (res && res.data) {
        usersData = res.data;
      } else {
        usersData = res;
      }
      
      console.log('📊 Users data received:', usersData);
      setUsers(usersData.users || []);
    } catch (err) {
      console.error("❌ Error fetching users:", err);
      console.error('Error details:', {
        message: err.message,
        status: err.response?.status,
        statusText: err.response?.statusText,
        data: err.response?.data
      });
    }
  };

  const fetchDeletedUsers = async () => {
    try {
      console.log('🔍 Fetching deleted users...');
      const res = await authFetch('/deleted-users');
      
      let deletedUsersData;
      if (res && res.data) {
        deletedUsersData = res.data;
      } else {
        deletedUsersData = res;
      }
      
      console.log('📊 Deleted users data received:', deletedUsersData);
      setDeletedUsers(deletedUsersData.users || []);
    } catch (err) {
      console.error("❌ Error fetching deleted users:", err);
      console.error('Error details:', {
        message: err.message,
        status: err.response?.status,
        statusText: err.response?.statusText,
        data: err.response?.data
      });
    }
  };

  const fetchUserInventoryStatus = async () => {
    try {
      console.log('🔍 Fetching user inventory status...');
      const res = await authFetch('/inventory');
      
      let inventoryData;
      if (res && res.data) {
        inventoryData = res.data;
      } else {
        inventoryData = res;
      }
      
      console.log('📊 Inventory data received:', inventoryData);
      
      // Group inventory items by assigned user
      const statusMap = {};
      if (Array.isArray(inventoryData)) {
        inventoryData.forEach(item => {
          if (item.assignedTo && item.assignedTo._id) {
            if (!statusMap[item.assignedTo._id]) {
              statusMap[item.assignedTo._id] = [];
            }
            statusMap[item.assignedTo._id].push(item);
          }
        });
      }
      
      console.log('📋 User inventory status map:', statusMap);
      setUserInventoryStatus(statusMap);
    } catch (err) {
      console.error("❌ Error fetching user inventory status:", err);
      console.error('Error details:', {
        message: err.message,
        status: err.response?.status,
        statusText: err.response?.statusText,
        data: err.response?.data
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const avatar = useDefaultAvatar
      ? "http://localhost:5001/images/iesavatar.jpg"
      : customAvatar;

    const payload = { ...formData, avatar };

    try {
      console.log('🔍 Creating user:', payload);
      
      await authFetch.post('/users', payload);

      console.log('✅ User created successfully');
      setFormData({ name: "", email: "", password: "", role: "Employee" });
      setCustomAvatar("");
      setShowModal(false);
      fetchUsers();
    } catch (err) {
      console.error("❌ Error creating user:", err);
      console.error('Error details:', {
        message: err.message,
        status: err.response?.status,
        statusText: err.response?.statusText,
        data: err.response?.data
      });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t('adminUsers.areYouSureDeleteUser'))) return;
    try {
      console.log('🔍 Deleting user:', id);
      
      await authFetch.delete(`/users/${id}`);
      
      console.log('✅ User deleted successfully');
      fetchUsers();
      fetchDeletedUsers();
    } catch (err) {
      console.error("❌ Error deleting user:", err);
      console.error('Error details:', {
        message: err.message,
        status: err.response?.status,
        statusText: err.response?.statusText,
        data: err.response?.data
      });
      
      // Handle specific error cases
      if (err.response?.status === 400 && err.response?.data?.assignedItems) {
        const itemsList = err.response.data.assignedItems.map(item => 
          `• ${item.type} - ${item.brand} ${item.model} (S/N: ${item.serialNumber})`
        ).join('\n');
        
        alert(t('adminUsers.cannotDeleteUserInventory'));
      } else {
        alert(err.response?.data?.message || t('adminUsers.failedToDeleteUser'));
      }
    }
  };

  const handleSpecialPermissionChange = (permissionKey, isEnabled) => {
    if (!selectedUser) return;

    console.log('🔍 Permission change:', {
      permissionKey,
      isEnabled,
      currentPending: pendingSpecialPermissions,
      selectedUserId: selectedUser._id,
      currentUserPermissions: selectedUser.employeeSpecialPermissions
    });

    // Update pending permissions state
    setPendingSpecialPermissions(prev => {
      console.log('🔍 Previous pending permissions:', prev);
      const newPending = {
        ...prev,
        [permissionKey]: isEnabled
      };
      console.log('🔍 New pending permissions:', newPending);
      console.log('🔍 All permission keys being set:', Object.keys(newPending));
      return newPending;
    });

    console.log(`📝 Special permission ${permissionKey} ${isEnabled ? 'enabled' : 'disabled'} (pending save) for user ${selectedUser.name}`);
  };

  const saveSpecialPermissions = async () => {
    if (!selectedUser) return;

    console.log('🔍 Saving special permissions:', {
      selectedUserId: selectedUser._id,
      selectedUserName: selectedUser.name,
      currentPermissions: selectedUser.employeeSpecialPermissions,
      pendingPermissions: pendingSpecialPermissions
    });

    setSavingPermissions(true);
    try {
      console.log('🔍 Building special permissions:', {
        originalPermissions: selectedUser.employeeSpecialPermissions,
        pendingPermissions: pendingSpecialPermissions,
        originalKeys: Object.keys(selectedUser.employeeSpecialPermissions || {}),
        pendingKeys: Object.keys(pendingSpecialPermissions)
      });

      const specialPermissions = {
        ...selectedUser.employeeSpecialPermissions,
        ...pendingSpecialPermissions
      };

      console.log('🔍 Final permissions to save:', specialPermissions);
      console.log('🔍 Final permissions keys:', Object.keys(specialPermissions));
      console.log('🔍 SelectedUser before update:', {
        id: selectedUser._id,
        name: selectedUser.name,
        effectivePermissions: selectedUser.effectivePermissions,
        employeeSpecialPermissions: selectedUser.employeeSpecialPermissions
      });

      await authFetch(`/users/special-permissions/${selectedUser._id}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ specialPermissions })
      });

      console.log(`✅ Special permissions saved successfully for user ${selectedUser.name}`);
      
      // Refresh user data from server to ensure we have the latest data
      try {
        const updatedUserRes = await authFetch(`/users/special-permissions/${selectedUser._id}`);
        const updatedUserData = updatedUserRes;
        
        console.log('🔍 Refreshed user data from server:', updatedUserData);
        console.log('🔍 Server response structure:', {
          hasData: !!updatedUserData.data,
          specialPermissions: updatedUserData.specialPermissions,
          effectivePermissions: updatedUserData.effectivePermissions,
          dataSpecialPermissions: updatedUserData.data?.specialPermissions,
          dataEffectivePermissions: updatedUserData.data?.effectivePermissions,
          fullResponseKeys: Object.keys(updatedUserData),
          dataKeys: updatedUserData.data ? Object.keys(updatedUserData.data) : 'no data'
        });
        
        // Extract the actual data - it might be nested in .data
        const actualSpecialPermissions = updatedUserData.data?.specialPermissions || updatedUserData.specialPermissions;
        const actualEffectivePermissions = updatedUserData.data?.effectivePermissions || updatedUserData.effectivePermissions;
        
        console.log('🔍 Extracted data:', {
          actualSpecialPermissions,
          actualEffectivePermissions
        });
        
        // Update the selected user with fresh data from server
        const updatedSelectedUser = {
          ...selectedUser,
          employeeSpecialPermissions: actualSpecialPermissions,
          effectivePermissions: actualEffectivePermissions
        };
        
        setSelectedUser(updatedSelectedUser);
        
        // Also update the users list with fresh data
        setUsers(prev => prev.map(user => 
          user._id === selectedUser._id 
            ? { 
                ...user, 
                employeeSpecialPermissions: actualSpecialPermissions,
                effectivePermissions: actualEffectivePermissions
              }
            : user
        ));
        
        console.log('🔍 Updated selectedUser:', updatedSelectedUser);
        console.log('🔍 User data refreshed from server successfully');
        
        // Clear pending changes AFTER refreshing from server
        setPendingSpecialPermissions({});
      } catch (refreshErr) {
        console.error('Error refreshing user data:', refreshErr);
        // Clear pending changes even if refresh fails
        setPendingSpecialPermissions({});
      }
      
      alert('Special permissions updated successfully!');
    } catch (err) {
      console.error('Error updating special permissions:', err);
      alert(t('adminUsers.failedToUpdatePermissions'));
    } finally {
      setSavingPermissions(false);
    }
  };

  const renderTable = (data) => (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-blue-100">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              {t('adminUsers.user')}
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              {t('adminUsers.email')}
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              {t('adminUsers.role')}
            </th>
            {activeTab === "active" && (
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t('adminUsers.actions')}
              </th>
            )}
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {data.length > 0 ? (
            data.map((user) => (
              <tr
                key={user._id}
                className="cursor-pointer hover:bg-gray-50 transition"
                onClick={() => handleOpenUserDetails(user)}
              >
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center gap-3">
                    {user.avatar ? (
                      <img src={user.avatar} alt="avatar" className="w-10 h-10 rounded-full" />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-gray-300" />
                    )}
                    <div className="text-sm font-medium text-gray-900">{user.name}</div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {user.email}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    {user.role === 'Admin' || user.role === 'SuperAdmin' ? t('adminUsers.admin') : t('adminUsers.employee')}
                  </span>
                </td>
                {activeTab === "active" && (
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex items-center gap-2">
                      {userInventoryStatus[user._id] && userInventoryStatus[user._id].length > 0 && (
                        <span 
                          className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800"
                          title={t('adminUsers.hasAssignedInventoryItems', { count: userInventoryStatus[user._id].length })}
                        >
                          📦 {userInventoryStatus[user._id].length}
                        </span>
                      )}
                      <button
                        onClick={e => { e.stopPropagation(); handleDelete(user._id); }}
                        className={`${userInventoryStatus[user._id] && userInventoryStatus[user._id].length > 0 
                          ? 'text-gray-400 cursor-not-allowed' 
                          : 'text-red-600 hover:text-red-900'}`}
                        title={userInventoryStatus[user._id] && userInventoryStatus[user._id].length > 0 
                          ? t('adminUsers.cannotDeleteAssignedInventory') 
                          : t('adminUsers.deleteUser')}
                        disabled={userInventoryStatus[user._id] && userInventoryStatus[user._id].length > 0}
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                )}
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan="4" className="px-6 py-12 text-center">
                <UsersIcon className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">{t('adminUsers.noUsersFound')}</h3>
                <p className="mt-1 text-sm text-gray-500">
                  {searchQuery 
                    ? t('adminUsers.tryAdjustingSearch')
                    : activeTab === "active" 
                      ? t('adminUsers.getStartedCreatingUser')
                      : t('adminUsers.noDeletedUsersToDisplay')
                  }
                </p>
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );

  // Open user details modal and fetch projects/assets
  const handleOpenUserDetails = async (user) => {
    console.log('🔍 Opening user details for:', {
      userName: user.name,
      userRole: user.role,
      employeeSpecialPermissions: user.employeeSpecialPermissions,
      effectivePermissions: user.effectivePermissions
    });
    
    // For Employee users, refresh their special permissions data from server
    if (user.role === 'Employee') {
      try {
        console.log('🔍 Refreshing employee special permissions from server...');
        const specialPermsRes = await authFetch(`/users/special-permissions/${user._id}`);
        
        console.log('🔍 Server response when opening modal:', specialPermsRes);
        
        // Extract the actual data - it might be nested in .data
        const actualSpecialPermissions = specialPermsRes.data?.specialPermissions || specialPermsRes.specialPermissions;
        const actualEffectivePermissions = specialPermsRes.data?.effectivePermissions || specialPermsRes.effectivePermissions;
        
        console.log('🔍 Extracted data when opening modal:', {
          actualSpecialPermissions,
          actualEffectivePermissions
        });
        
        const freshUserData = {
          ...user,
          employeeSpecialPermissions: actualSpecialPermissions,
          effectivePermissions: actualEffectivePermissions
        };
        
        console.log('🔍 Fresh user data from server:', freshUserData);
        setSelectedUser(freshUserData);
      } catch (err) {
        console.error('Error refreshing employee data:', err);
        setSelectedUser(user); // Fallback to original user data
      }
    } else {
      setSelectedUser(user);
    }
    
    setPendingSpecialPermissions({}); // Reset pending permissions when opening modal
    setLoadingDetails(true);
    try {
      console.log('🔍 Fetching details for user:', user.name);
      
      // Fetch user's assigned projects
      const projectsRes = await authFetch(`/users/${user._id}/projects`);
      
      let projectsData;
      if (projectsRes && projectsRes.data) {
        projectsData = projectsRes.data;
      } else {
        projectsData = projectsRes;
      }
      
      console.log('📊 User projects data received:', projectsData);
      setUserProjects(projectsData.assignedProjects || []);

      // Fetch user's assigned inventory items
      const inventoryRes = await authFetch(`/users/${user._id}/inventory`);
      
      let inventoryData;
      if (inventoryRes && inventoryRes.data) {
        inventoryData = inventoryRes.data;
      } else {
        inventoryData = inventoryRes;
      }
      
      console.log('📊 User inventory data received:', inventoryData);
      setUserAssets(inventoryData.assignedItems || []);
      
    } catch (err) {
      console.error('❌ Error fetching user details:', err);
      console.error('Error details:', {
        message: err.message,
        status: err.response?.status,
        statusText: err.response?.statusText,
        data: err.response?.data
      });
      setUserProjects([]);
      setUserAssets([]);
    } finally {
      setLoadingDetails(false);
    }
  };


  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center pt-16">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">{t('adminUsers.usersManagement')}</h1>
          <p className="text-gray-600 mt-2">{t('adminUsers.manageOrganizationUsers')}</p>
        </div>
        {activeTab === "active" && (
          <button
            onClick={() => setShowModal(true)}
            className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-semibold flex items-center gap-2 transition-colors"
          >
            <PlusCircle size={20} />
            {t('adminUsers.addNewUser')}
          </button>
        )}
      </div>

      {/* Search Bar */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
          <input
            type="text"
            placeholder={t('adminUsers.searchUsers')}
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('active')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'active'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <div className="flex items-center gap-2">
              <UsersIcon size={16} />
              {t('adminUsers.currentUsers')}
            </div>
          </button>
          <button
            onClick={() => setActiveTab('deleted')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'deleted'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <div className="flex items-center gap-2">
              <Trash2 size={16} />
              {t('adminUsers.deletedUsers')}
            </div>
          </button>
        </nav>
      </div>

      {/* Users Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden border border-gray-200">
        {activeTab === "active" ? renderTable(displayedUsers) : renderTable(displayedUsers)}
      </div>

      {/* Summary */}
      <div className="text-sm text-gray-600 font-semibold">
        {t('adminUsers.totalUsers')}: {displayedUsers.length}
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {t('adminUsers.addNewUser')}
              </h3>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('adminUsers.fullName')}</label>
                  <input 
                    type="text" 
                    name="name" 
                    value={formData.name} 
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })} 
                    placeholder={t('adminUsers.enterFullName')} 
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent" 
                    required 
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('adminUsers.email')}</label>
                  <input 
                    type="email" 
                    name="email" 
                    value={formData.email} 
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })} 
                    placeholder={t('adminUsers.enterEmailAddress')} 
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent" 
                    required 
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('adminUsers.password')}</label>
                  <input 
                    type="password" 
                    name="password" 
                    value={formData.password} 
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })} 
                    placeholder={t('adminUsers.enterPassword')} 
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent" 
                    required 
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('adminUsers.role')}</label>
                  <select
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    name="role"
                    value={formData.role}
                    onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                    required
                  >
                    <option value="Employee">{t('adminUsers.employee')}</option>
                    <option value="Admin">{t('adminUsers.admin')}</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('adminUsers.avatar')}</label>
                  <div className="space-y-2">
                    <label className="flex items-center gap-2">
                      <input 
                        type="radio" 
                        name="avatarChoice" 
                        checked={useDefaultAvatar} 
                        onChange={() => setUseDefaultAvatar(true)}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                      />
                      <span className="text-sm text-gray-700">{t('adminUsers.useDefaultAvatar')}</span>
                    </label>
                    <label className="flex items-center gap-2">
                      <input 
                        type="radio" 
                        name="avatarChoice" 
                        checked={!useDefaultAvatar} 
                        onChange={() => setUseDefaultAvatar(false)}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                      />
                      <span className="text-sm text-gray-700">{t('adminUsers.uploadCustomAvatar')}</span>
                    </label>
                    {!useDefaultAvatar && (
                      <input 
                        type="text" 
                        value={customAvatar} 
                        onChange={(e) => setCustomAvatar(e.target.value)} 
                        placeholder={t('adminUsers.enterImageURL')} 
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent" 
                      />
                    )}
                  </div>
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300"
                  >
                    {t('adminUsers.cancel')}
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                  >
                    {t('adminUsers.createUser')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* User Details Modal */}
      {selectedUser && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-4/5 max-w-4xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900">{t('adminUsers.userDetails')}</h3>
                <button
                  onClick={() => setSelectedUser(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <span className="text-2xl">&times;</span>
                </button>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div className="flex items-center gap-4">
                    <img 
                      src={selectedUser.avatar || 'http://localhost:5001/images/iesavatar.jpg'} 
                      alt="avatar" 
                      className="w-16 h-16 rounded-full border-2 border-gray-200" 
                    />
                    <div>
                      <h4 className="text-xl font-semibold text-gray-900">{selectedUser.name}</h4>
                      <p className="text-sm text-gray-500">{selectedUser.role === 'Admin' || selectedUser.role === 'SuperAdmin' ? t('adminUsers.admin') : t('adminUsers.employee')}</p>
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700">{t('adminUsers.email')}</label>
                    <p className="text-sm text-gray-900">{selectedUser.email}</p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700">{t('adminUsers.role')}</label>
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      {selectedUser.role === 'Admin' || selectedUser.role === 'SuperAdmin' ? t('adminUsers.admin') : t('adminUsers.employee')}
                    </span>
                  </div>

                  {/* Special Permissions Section for Employees */}
                  {selectedUser.role === 'Employee' && (
                    <div>
                      {console.log('🔍 Rendering special permissions section for employee:', {
                        userName: selectedUser.name,
                        currentPermissions: selectedUser.employeeSpecialPermissions,
                        pendingPermissions: pendingSpecialPermissions
                      })}
                      <label className="block text-sm font-medium text-gray-700 mb-2">{t('adminUsers.specialPermissions')}</label>
                      <div className="space-y-2">
                        {Object.entries({
                          autopark: t('adminUsers.autoparkAccess'),
                          inventory: t('adminUsers.inventoryAccess'),
                          contracts: t('adminUsers.contractsAccess'),
                          workers: t('adminUsers.workersAccess'),
                          invoices: t('adminUsers.invoicesAccess'),
                          analytics: t('adminUsers.analyticsAccess'),
                          forecasting: t('adminUsers.forecastingAccess'),
                          resources: t('adminUsers.resourcesAccess'),
                          users: t('adminUsers.usersAccess'),
                          settings: t('adminUsers.settingsAccess')
                        }).map(([key, label]) => {
                          const currentValue = selectedUser.employeeSpecialPermissions?.[key] || false;
                          const pendingValue = pendingSpecialPermissions[key];
                          const displayValue = pendingValue !== undefined ? pendingValue : currentValue;
                          
                          console.log(`🔍 Checkbox ${key}:`, {
                            currentValue,
                            pendingValue,
                            displayValue,
                            label,
                            allPendingKeys: Object.keys(pendingSpecialPermissions),
                            allPendingValues: pendingSpecialPermissions
                          });
                          
                          return (
                            <label key={key} className="flex items-center gap-2">
                              <input
                                type="checkbox"
                                checked={displayValue}
                                onChange={(e) => handleSpecialPermissionChange(key, e.target.checked)}
                                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                              />
                              <span className="text-sm text-gray-700">{label}</span>
                              {pendingValue !== undefined && (
                                <span className="text-xs text-blue-600 font-medium">(pending)</span>
                              )}
                            </label>
                          );
                        })}
                      </div>
                      <p className="text-xs text-gray-500 mt-2">
                        {t('adminUsers.specialPermissionsNote')}
                      </p>
                    </div>
                  )}
                </div>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">{t('adminUsers.assignedProjects')}</label>
                    {loadingDetails ? (
                      <div className="text-gray-400 text-sm">{t('adminUsers.loading')}</div>
                    ) : userProjects.length === 0 ? (
                      <div className="text-gray-400 text-sm">{t('adminUsers.noAssignedProjects')}</div>
                    ) : (
                      <div className="space-y-1">
                        {userProjects.map(p => (
                          <div key={p._id} className="text-sm bg-gray-50 p-2 rounded-md">
                            {p.name}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">{t('adminUsers.assignedAssets')}</label>
                    {loadingDetails ? (
                      <div className="text-gray-400 text-sm">{t('adminUsers.loading')}</div>
                    ) : userAssets.length === 0 ? (
                      <div className="text-gray-400 text-sm">{t('adminUsers.noAssetsAssigned')}</div>
                    ) : (
                      <div className="space-y-1">
                        {userAssets.map(a => (
                          <div key={a._id} className="text-sm bg-gray-50 p-2 rounded-md">
                            {a.type} {a.brand} {a.model} (S/N: {a.serialNumber})
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
              
              <div className="flex justify-between pt-4 mt-6 border-t border-gray-200">
                <div>
                  {Object.keys(pendingSpecialPermissions).length > 0 && (
                    <span className="text-sm text-blue-600 font-medium">
                      {t('adminUsers.unsavedChanges')}
                    </span>
                  )}
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={() => {
                      setPendingSpecialPermissions({});
                      setSelectedUser(null);
                    }}
                    className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
                  >
                    {t('adminUsers.close')}
                  </button>
                  {selectedUser.role === 'Employee' && Object.keys(pendingSpecialPermissions).length > 0 && (
                    <button
                      onClick={saveSpecialPermissions}
                      disabled={savingPermissions}
                      className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-blue-400 disabled:cursor-not-allowed"
                    >
                      {savingPermissions ? t('adminUsers.saving') : t('adminUsers.saveChanges')}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const AdminUsers = () => {
  return (
    <ProtectedRoute pageKey="users" requireAdmin={true}>
      <AdminUsersContent />
    </ProtectedRoute>
  );
};

export default AdminUsers;
